import { useState, useRef } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

/**
 * <Slopulation />
 *
 * Renders the coined term "general slopulation" as a semantically correct
 * <dfn> element with three layers of clarification for anyone (or any
 * crawler) who mistakes it for a typo of "population":
 *
 *   1. Native `title` attribute        — inline definition for legacy crawlers
 *   2. Radix Popover on hover / tap    — visible in-place definition for humans
 *   3. `id="slopulation"` anchor       — deep-linkable from /faq#slopulation
 *
 * Screen readers announce it as "definition, general slopulation". The
 * dotted underline is the visual affordance signalling "this is interactive"
 * so mobile users know to tap.
 */
const DEFINITION_SHORT =
    "Deliberate coinage — slop + population. The sedentary, unoptimized average that standard lab reference ranges are derived from.";

export default function Slopulation() {
    const [open, setOpen] = useState(false);
    const closeTimer = useRef(null);

    const scheduleClose = () => {
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), 180);
    };

    const cancelClose = () => {
        clearTimeout(closeTimer.current);
    };

    // Hover open/close only for mouse — touch devices use the native
    // Popover click toggle, otherwise pointerLeave on tap-release would
    // close the popover 180ms after the tap opens it.
    const isMouse = (e) => e.pointerType === "mouse";

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <dfn
                    id="slopulation"
                    title={DEFINITION_SHORT}
                    tabIndex={0}
                    aria-label="Definition: general slopulation"
                    data-testid="slopulation-term"
                    onPointerEnter={(e) => {
                        if (!isMouse(e)) return;
                        cancelClose();
                        setOpen(true);
                    }}
                    onPointerLeave={(e) => {
                        if (!isMouse(e)) return;
                        scheduleClose();
                    }}
                    className="cursor-help not-italic underline decoration-dotted decoration-white/40 decoration-1 underline-offset-[6px] hover:decoration-[#49bef5] focus-visible:outline-none focus-visible:decoration-[#49bef5] transition-colors"
                >
                    general slopulation
                </dfn>
            </PopoverTrigger>
            <PopoverContent
                side="top"
                sideOffset={10}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
                onPointerEnter={(e) => {
                    if (!isMouse(e)) return;
                    cancelClose();
                }}
                onPointerLeave={(e) => {
                    if (!isMouse(e)) return;
                    scheduleClose();
                }}
                className="max-w-[300px] w-[300px] border border-white/15 bg-[#0a0a0a]/95 backdrop-blur-md text-white/85 shadow-[0_20px_60px_-15px_rgba(43,166,224,0.35)] rounded-xl p-4"
                data-testid="slopulation-popover"
            >
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#49bef5] mb-2">
                    Terminology
                </p>
                <p className="text-sm text-white font-medium mb-1.5">
                    "General slopulation" is intentional.
                </p>
                <p className="text-sm text-white/70 leading-relaxed">
                    A deliberate coinage — <em>slop</em> + <em>population</em>.
                    The sedentary, unoptimized average that standard lab
                    reference ranges are derived from.
                </p>
            </PopoverContent>
        </Popover>
    );
}
